import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { fetchEndpointResult } from 'api';
import { useEffectOnce, useGlobalLinks, useRequiredContext } from 'hooks';
import { usePath } from 'hooks/navigation';
import type { MinimizedAddToWishlistResponse, Wishlist } from 'models/api';
import { ignorePromiseRejection } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import { useSelectedStore } from './SelectedStoreContextProvider';
import { useToastContext } from './ToastContextProvider';

interface WishlistContextType {
	addedProductPopoverData: MinimizedAddToWishlistResponse | undefined;
	clearWishlist: () => void;
	closeAddedProductPopover: () => void;
	isInitialLoading: boolean;
	loadFullWishlist: () => void;
	openAddedProductPopover: (data: MinimizedAddToWishlistResponse) => void;
	quantity: number | undefined;
	updateTotalQuantity: (quantity: number) => void;
	updateWishlist: (wishlist: Wishlist) => void;
	wishlist: Wishlist | undefined;
}

export const WishlistContext = createContext<WishlistContextType | undefined>(
	undefined,
);

interface Props {
	children: ReactNode;
}

export function WishlistContextProvider({ children }: Props) {
	const { t } = useI18n();
	const { wishlist: wishlistPage } = useGlobalLinks();

	const path = usePath();
	const { sendToast } = useToastContext();
	const { selectedStore } = useSelectedStore();
	const [quantity, setQuantity] = useState<number>();
	const [wishlist, setWishlist] = useState<Wishlist>();
	const [addedProductPopoverData, setAddedProductPopoverData] = useState<
		MinimizedAddToWishlistResponse | undefined
	>();
	const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

	const loadFullWishlist = useCallback(async () => {
		const result = await fetchEndpointResult({
			name: 'PostWishlist',
			queryParams: { storeId: selectedStore?.id },
		});
		if (result.isFailure()) {
			sendToast({
				message: t('general_critical_error_text'),
				variant: 'error',
			});
			return;
		}
		setIsInitialLoading(false);
		if (result.value) {
			setWishlist(result.value);
			setQuantity(result.value.totalQty);
		}
	}, [selectedStore?.id, sendToast, t]);

	const loadMiniWishlist = useCallback(async () => {
		const result = await fetchEndpointResult('GetWishlistMini');
		if (result.isFailure()) {
			sendToast({
				message: t('general_critical_error_text'),
				variant: 'error',
			});
			return;
		}
		setIsInitialLoading(false);
		if (result.value) {
			setQuantity(result.value.totalQty);
		}
	}, [sendToast, t]);

	useEffectOnce(() => {
		if (path.startsWith(wishlistPage)) {
			ignorePromiseRejection(loadFullWishlist());
		} else {
			ignorePromiseRejection(loadMiniWishlist());
		}
	});

	const updateTotalQuantity = useCallback((newQuantity: number) => {
		setQuantity(newQuantity);
	}, []);

	const updateWishlist = useCallback((updatedWishlist: Wishlist) => {
		setWishlist(updatedWishlist);
	}, []);

	const clearWishlist = useCallback(() => {
		setWishlist(undefined);
		setQuantity(0);
	}, []);

	const openAddedProductPopover = useCallback(
		(data: MinimizedAddToWishlistResponse) => {
			setAddedProductPopoverData(data);
		},
		[],
	);

	const closeAddedProductPopover = useCallback(() => {
		setAddedProductPopoverData(undefined);
	}, []);

	const value = useMemo(
		() => ({
			addedProductPopoverData,
			clearWishlist,
			closeAddedProductPopover,
			isInitialLoading,
			loadFullWishlist,
			openAddedProductPopover,
			quantity,
			updateTotalQuantity,
			updateWishlist,
			wishlist,
		}),
		[
			addedProductPopoverData,
			clearWishlist,
			closeAddedProductPopover,
			isInitialLoading,
			loadFullWishlist,
			openAddedProductPopover,
			quantity,
			updateTotalQuantity,
			updateWishlist,
			wishlist,
		],
	);

	return <WishlistContext value={value}>{children}</WishlistContext>;
}

WishlistContextProvider.displayName = 'WishlistContextProvider';

export function useWishlistContext() {
	return useRequiredContext(WishlistContext, 'WishlistContext');
}
